import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle, XCircle, ExternalLink, Clock, AlertTriangle, Loader2, CheckCheck } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

// Use relative URLs for API calls
const API_URL = '';

const PendingPaymentVerifications = ({ reviewerId }) => {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [verifyingAll, setVerifyingAll] = useState(false);
  const { toast } = useToast();

  // Generate Cash App URL - just opens Cash App home/download page
  // Activity page is only accessible within the mobile app
  const getCashAppUrl = () => {
    // Cash App doesn't have a web-based activity page
    // This opens the Cash App website which prompts to open the app
    return 'https://cash.app';
  };

  const fetchPendingPayments = useCallback(async () => {
    if (!reviewerId) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/payments/pending-verifications/${reviewerId}`);
      setPendingPayments(response.data.payments || []);
    } catch (error) {
      console.error('Error fetching pending payments:', error);
    } finally {
      setLoading(false);
    }
  }, [reviewerId]);

  useEffect(() => {
    fetchPendingPayments();
    // Poll every 10 seconds for new pending payments
    const interval = setInterval(fetchPendingPayments, 10000);
    return () => clearInterval(interval);
  }, [fetchPendingPayments]);

  const handleVerify = async (paymentId) => {
    setProcessingId(paymentId);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/api/payments/verify-cashapp-payment`,
        { payment_id: paymentId, action: 'verify' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "✅ Payment Verified!",
        description: `$${(response.data.amount / 100).toFixed(2)} added to your earnings.`,
        variant: "default"
      });
      
      // Remove from list
      setPendingPayments(prev => prev.filter(p => p.payment_id !== paymentId));
    } catch (error) {
      console.error('Error verifying payment:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to verify payment",
        variant: "destructive"
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleVerifyAll = async () => {
    if (pendingPayments.length === 0) return;
    
    setVerifyingAll(true);
    const token = localStorage.getItem('access_token');
    let verified = 0;
    let totalEarnings = 0;
    
    for (const payment of pendingPayments) {
      try {
        const response = await axios.post(
          `${API_URL}/api/payments/verify-cashapp-payment`,
          { payment_id: payment.payment_id, action: 'verify' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        verified++;
        totalEarnings += response.data.amount / 100;
      } catch (error) {
        console.error(`Error verifying payment ${payment.payment_id}:`, error);
      }
    }
    
    toast({
      title: "✅ All Payments Verified!",
      description: `${verified} payments verified. $${totalEarnings.toFixed(2)} added to your earnings.`,
      variant: "default"
    });
    
    setPendingPayments([]);
    setVerifyingAll(false);
  };

  const handleReject = async (paymentId) => {
    setProcessingId(paymentId);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/api/payments/verify-cashapp-payment`,
        { 
          payment_id: paymentId, 
          action: 'reject',
          rejection_reason: 'Payment not received'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Moved to Regular Queue",
        description: "Payment not verified. Submission moved to the end of your queue.",
        variant: "default"
      });
      
      // Remove from pending list
      setPendingPayments(prev => prev.filter(p => p.payment_id !== paymentId));
    } catch (error) {
      console.error('Error processing payment:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to process payment",
        variant: "destructive"
      });
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return null;
  }

  if (pendingPayments.length === 0) {
    return null;
  }

  const getPaymentTypeLabel = (type) => {
    switch (type) {
      case 'custom': return { label: 'Custom', color: 'bg-green-600' };
      case 'superskip': return { label: 'Super', color: 'bg-red-600' };
      case 'skip': return { label: 'Skip', color: 'bg-yellow-600' };
      default: return { label: type, color: 'bg-gray-600' };
    }
  };

  // Don't render anything if no pending payments
  if (!loading && pendingPayments.length === 0) {
    return null;
  }

  return (
    <Card className="bg-yellow-900/30 border-yellow-600/50 mb-4">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-yellow-200 text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            {loading ? 'Loading...' : `Verify Payments (${pendingPayments.length})`}
          </CardTitle>
          
          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(getCashAppUrl(), '_blank')}
              className="border-green-500 text-green-400 hover:bg-green-500/20"
              title="Check your Cash App for incoming payments"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Check Cash App
            </Button>
            
            {pendingPayments.length > 1 && (
              <Button
                size="sm"
                onClick={handleVerifyAll}
                disabled={verifyingAll}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {verifyingAll ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <CheckCheck className="h-4 w-4 mr-1" />
                )}
                Verify All
              </Button>
            )}
          </div>
        </div>
        <p className="text-yellow-200/70 text-sm mt-1">
          Check your Cash App or Venmo, then verify each payment received.
        </p>
      </CardHeader>
      
      {/* Pro Tip for Reviewers */}
      <div className="px-6 pb-2">
        <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
          <p className="text-gray-300 text-xs">
            💡 <strong>Pro Tip:</strong> Enable Cash App &amp; Venmo notifications on your phone so you&apos;ll see payments instantly!
          </p>
        </div>
      </div>
      
      <CardContent className="space-y-2">
        {pendingPayments.map((payment) => {
          const typeInfo = getPaymentTypeLabel(payment.payment_type);
          
          return (
            <div 
              key={payment.payment_id}
              className="bg-gray-800/50 rounded-lg p-3 flex items-center justify-between"
            >
              {/* Left side - Payment info */}
              <div className="flex items-center gap-3">
                <div className="text-center min-w-[60px]">
                  <Badge className={`${typeInfo.color} text-white text-xs`}>
                    {typeInfo.label}
                  </Badge>
                  <p className="text-white font-bold mt-1">
                    {payment.amount_display}
                  </p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm truncate">
                    {payment.submission_artist || 'Artist'}
                  </p>
                  {/* Show payment method name (Cash App or Venmo) */}
                  {payment.artist_cash_app_name && (
                    <p className={`text-xs truncate ${payment.payment_method === 'venmo' ? 'text-blue-400' : 'text-green-400'}`}>
                      {payment.payment_method === 'venmo' ? 'Venmo' : 'Cash App'}: {payment.artist_cash_app_name}
                    </p>
                  )}
                  <p className="text-gray-400 text-xs flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {payment.artist_confirmed_at 
                      ? new Date(payment.artist_confirmed_at).toLocaleTimeString('en-US', {
                          hour: '2-digit', 
                          minute: '2-digit',
                          timeZone: 'America/New_York'
                        })
                      : 'Just now'
                    }
                  </p>
                </div>
              </div>
              
              {/* Right side - Quick action buttons */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleReject(payment.payment_id)}
                  disabled={processingId === payment.payment_id}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0"
                  title="Not received - Move to regular queue"
                >
                  {processingId === payment.payment_id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <XCircle className="h-5 w-5" />
                  )}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleVerify(payment.payment_id)}
                  disabled={processingId === payment.payment_id}
                  className="bg-green-600 hover:bg-green-700 text-white h-8 px-3"
                >
                  {processingId === payment.payment_id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Verify
                    </>
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default PendingPaymentVerifications;
